<template>
  <div class="agent-availability-toggle flex flex-col items-start gap-1">
    <div class="inline-flex items-center gap-2">
    <div class="relative inline-flex items-center">
      <button
        type="button"
        role="switch"
        :aria-checked="isAvailable"
        :disabled="loading || !isValidProps"
        :class="[
          'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary',
          isAvailable ? 'bg-primary' : 'bg-gray-300',
          (loading || !isValidProps) && 'cursor-not-allowed opacity-70'
        ]"
        @click="handleToggle"
      >
        <span
          :class="[
            'pointer-events-none block h-5 w-5 rounded-full bg-white shadow transition-transform',
            isAvailable ? 'translate-x-5' : 'translate-x-1'
          ]"
          aria-hidden="true"
        />
      </button>
      <span
        v-if="loading"
        class="absolute -right-6 top-1/2 h-4 w-4 -translate-y-1/2"
        aria-hidden="true"
      >
        <svg
          class="h-4 w-4 animate-spin text-gray-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </span>
    </div>
    <span class="text-sm" aria-live="polite">
      {{ isAvailable ? 'Available' : 'Offline' }}
    </span>
    </div>
    <p
      v-if="error"
      class="text-sm text-red-600"
      role="alert"
    >
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { agentAPI } from '../services/api'

const props = defineProps({
  userId: {
    type: Number,
    required: true
  },
  userAgentTypeId: {
    type: Number,
    required: true
  }
})

const isAvailable = ref(false)
const loading = ref(false)
const error = ref(null)

const isValidProps = computed(() => {
  const uid = Number(props.userId)
  const typeId = Number(props.userAgentTypeId)
  return Number.isFinite(uid) && Number.isFinite(typeId) && uid >= 1 && typeId >= 1
})

function getTriggerAction (value) {
  return value ? 'AVAILABLE' : 'OFFLINE'
}

async function fetchCurrentStatus () {
  const uid = Number(props.userId)
  const typeId = Number(props.userAgentTypeId)
  if (!Number.isFinite(uid) || !Number.isFinite(typeId) || uid < 1 || typeId < 1) return
  loading.value = true
  error.value = null
  try {
    const res = await agentAPI.getCurrentStatus(uid, typeId)
    const action = res?.data?.data?.trigger_action
    isAvailable.value = action === 'AVAILABLE'
  } catch (err) {
    const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load status'
    error.value = msg
    isAvailable.value = false
  } finally {
    loading.value = false
  }
}

async function handleToggle () {
  if (loading.value) return
  const next = !isAvailable.value
  const prev = isAvailable.value
  isAvailable.value = next
  error.value = null
  loading.value = true
  try {
    await agentAPI.toggleStatus({
      user_id: props.userId,
      user_agent_type_id: props.userAgentTypeId,
      trigger_action: getTriggerAction(next)
    })
  } catch (err) {
    isAvailable.value = prev
    const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to update status'
    error.value = msg
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchCurrentStatus()
})

watch(
  () => [props.userId, props.userAgentTypeId],
  () => {
    fetchCurrentStatus()
  },
  { deep: true }
)
</script>
